import React, { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Box, useMediaSize } from '@ally/metronome-ui'
import logRocket from 'logrocket'
import { validateTiles } from '../utils'
import { AdobeResponse, AdobeTileContent, MboxResponseObj } from '../types'
import { defaultTiles } from './defaultTiles'
import XLExploreTiles from './XL/index'
import LgExploreTiles from './LG/index'
import MdSmExploreTiles from './MdSm/index'
import { NonPrintingWrapper } from '../Shared/NonPrintingWrapper'

const DrawerEffectWrapper = styled(Box)`
  height: auto;
  -webkit-transition: max-height 1s ease-in-out;
  -moz-transition: max-height 1s ease-in-out;
  -ms-transition: max-height 1s ease-in-out;
  -o-transition: max-height 1s ease-in-out;
  transition: max-height 1s ease-in-out;
`

const ExploreTiles: FC<{ AboveAppPortal: FC }> = React.memo(
  ({ AboveAppPortal }) => {
    const [exploreTiles, setExploreTiles] = useState<AdobeTileContent | null>(
      null,
    )
    const { adobe } = window
    const requiredTileKeys = [
      'ariaText',
      'ctaHref',
      'ctaLabel',
      'description',
      'iconName',
      'lobName',
      'subtitle',
      'title',
    ]

    useEffect(() => {
      async function callMbox(): Promise<void> {
        await adobe.target.getOffer({
          mbox: 'ally-dashboard-explore-tiles',
          success: (response: AdobeResponse<AdobeTileContent>) => {
            if (!Array.isArray(response)) {
              logRocket.captureMessage('Adobe response invalid format', {
                tags: {
                  page: 'dashboard',
                },
                extra: {
                  response,
                },
              })
              return setExploreTiles(defaultTiles)
            }
            const mboxSetJson = response.filter(
              item => item.action === 'setJson',
            )[0] as MboxResponseObj<AdobeTileContent>
            const tilesAreValid =
              mboxSetJson &&
              mboxSetJson.content[0] &&
              validateTiles(mboxSetJson.content[0].tiles, requiredTileKeys) &&
              mboxSetJson.content[0].title
            const validResponse = tilesAreValid
              ? mboxSetJson.content[0]
              : defaultTiles
            return setExploreTiles(validResponse)
          },
          error: () => {
            return setExploreTiles(defaultTiles)
          },
          timeout: 2000,
        })
      }

      if (!adobe) setExploreTiles(defaultTiles)
      else callMbox()
    }, [adobe])

    const isXlOnly = useMediaSize('XlOnly')
    const isLgUp = useMediaSize('LgUp')

    const viewSize = (): string => {
      switch (true) {
        case isXlOnly:
          return 'xl'
        case isLgUp:
          return 'lg'
        default:
          return 'mdSm'
      }
    }

    return (
      <>
        <AboveAppPortal>
          <NonPrintingWrapper>
            <DrawerEffectWrapper
              maxHeight={viewSize() === 'xl' && exploreTiles ? '150px' : '0'}
              overflow={
                viewSize() === 'xl' && exploreTiles ? 'visible' : 'hidden'
              }
            >
              {viewSize() === 'xl' && exploreTiles ? (
                <XLExploreTiles
                  exploreTiles={exploreTiles}
                  AboveAppPortal={AboveAppPortal}
                />
              ) : null}
            </DrawerEffectWrapper>
          </NonPrintingWrapper>
        </AboveAppPortal>

        {viewSize() === 'lg' && exploreTiles ? (
          <NonPrintingWrapper>
            <LgExploreTiles exploreTiles={exploreTiles} />
          </NonPrintingWrapper>
        ) : null}

        {viewSize() === 'mdSm' && exploreTiles ? (
          <NonPrintingWrapper>
            <MdSmExploreTiles exploreTiles={exploreTiles} />
          </NonPrintingWrapper>
        ) : null}
      </>
    )
  },
)

export default ExploreTiles
